// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { ExportActions, ExportActionTypes } from 'actions/export-actions';
import { omit } from 'lodash';

import { ExportState } from '.';
import { defineActititiesField } from './import-reducer';

const defaultState: ExportState = {
    projects: {
        dataset: {
            current: {},
            modalInstance: null,
        },
        backup: {
            modalInstance: null,
            current: {},
        },
        blender: {
            current: {},
            modalInstance: null,
        },
    },
    tasks: {
        dataset: {
            current: {},
            modalInstance: null,
        },
        backup: {
            modalInstance: null,
            current: {},
        },
        blender: {
            current: {},
            modalInstance: null,
        },
    },
    references: {
        dataset: {
            current: {},
            modalInstance: null,
        },
        backup: {
            modalInstance: null,
            current: {},
        },
        blender: {
            current: {},
            modalInstance: null,
        },
    },
    jobs: {
        dataset: {
            current: {},
            modalInstance: null,
        },
        blender: {
            current: {},
            modalInstance: null,
        },
    },
    instanceType: null,
};

export default (state: ExportState = defaultState, action: ExportActions): ExportState => {
    switch (action.type) {
        case ExportActionTypes.OPEN_EXPORT_DATASET_MODAL: {
            const { instance } = action.payload;
            const activitiesField = defineActititiesField(instance);

            return {
                ...state,
                [activitiesField]: {
                    ...state[activitiesField],
                    dataset: {
                        ...state[activitiesField].dataset,
                        modalInstance: instance,
                    },
                },
                instanceType: activitiesField.slice(0, activitiesField.length - 1) as
                    | 'project'
                    | 'task'
                    | 'job'
                    | 'reference',
            };
        }
        case ExportActionTypes.CLOSE_EXPORT_DATASET_MODAL: {
            const { instance } = action.payload;
            const activitiesField = defineActititiesField(instance);

            return {
                ...state,
                [activitiesField]: {
                    ...state[activitiesField],
                    dataset: {
                        ...state[activitiesField].dataset,
                        modalInstance: null,
                    },
                },
                instanceType: null,
            };
        }
        case ExportActionTypes.EXPORT_DATASET: {
            const { instance, format } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'jobs' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    dataset: {
                        ...state[field].dataset,
                        current: {
                            ...state[field].dataset.current,
                            [instance.id]: !state[field].dataset.current[instance.id]
                                ? [format]
                                : [...state[field].dataset.current[instance.id], format],
                        },
                    },
                },
            };
        }
        case ExportActionTypes.EXPORT_DATASET_FAILED:
        case ExportActionTypes.EXPORT_DATASET_SUCCESS: {
            const { instance, format } = action.payload;
            const field: 'projects' | 'tasks' | 'jobs' | 'references' = defineActititiesField(instance);
            // No deep copy: it would turn an open modal's instance into a plain object
            const { dataset } = state[field];

            return {
                ...state,
                [field]: {
                    ...state[field],
                    dataset: {
                        ...dataset,
                        current: {
                            ...dataset.current,
                            [instance.id]: (dataset.current[instance.id] || []).filter(
                                (exporterName: string): boolean => exporterName !== format,
                            ),
                        },
                    },
                },
            };
        }
        case ExportActionTypes.OPEN_EXPORT_BACKUP_MODAL: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    backup: {
                        ...state[field].backup,
                        modalInstance: instance,
                    },
                },
                instanceType: field.slice(0, field.length - 1) as 'project' | 'task' | 'reference',
            };
        }
        case ExportActionTypes.CLOSE_EXPORT_BACKUP_MODAL: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    backup: {
                        ...state[field].backup,
                        modalInstance: null,
                    },
                },
                instanceType: null,
            };
        }
        case ExportActionTypes.EXPORT_BACKUP: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    backup: {
                        ...state[field].backup,
                        current: {
                            ...state[field].backup.current,
                            [instance.id]: true,
                        },
                    },
                },
            };
        }
        case ExportActionTypes.EXPORT_BACKUP_FAILED:
        case ExportActionTypes.EXPORT_BACKUP_SUCCESS: {
            const { instance } = action.payload;

            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    backup: {
                        ...state[field].backup,
                        current: omit(state[field].backup, instance.id),
                    },
                },
            };
        }
        case ExportActionTypes.OPEN_EXPORT_BLENDER_MODAL: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    blender: {
                        ...state[field].blender,
                        modalInstance: instance,
                    },
                },
                instanceType: field.slice(0, field.length - 1) as 'project' | 'task' | 'reference',
            };
        }
        case ExportActionTypes.CLOSE_EXPORT_BLENDER_MODAL: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    blender: {
                        ...state[field].blender,
                        modalInstance: null,
                    },
                },
                instanceType: null,
            };
        }
        case ExportActionTypes.EXPORT_BLENDER: {
            const { instance } = action.payload;
            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    blender: {
                        ...state[field].blender,
                        current: {
                            ...state[field].blender.current,
                            [instance.id]: true,
                        },
                    },
                },
            };
        }
        case ExportActionTypes.EXPORT_BLENDER_FAILED:
        case ExportActionTypes.EXPORT_BLENDER_SUCCESS: {
            const { instance } = action.payload;

            const field = defineActititiesField(instance) as 'projects' | 'tasks' | 'references';

            return {
                ...state,
                [field]: {
                    ...state[field],
                    blender: {
                        ...state[field].blender,
                        current: omit(state[field].blender, instance.id),
                    },
                },
            };
        }
        default:
            return state;
    }
};
